/**
 * The saved views of the order table (design/04 §6, R19) — one tap shows exactly the orders of one
 * kind of work. Views that order-service knows by name (`view=`, counted for the to-do inbox) go as
 * `filter[view]`; the others are plain filters. Ids are stable: the home page links to them
 * (`/orders?view=ready_to_ship&filter[…]`).
 */
import type { TFunction } from "i18next";

import type { FilterState, SavedView } from "@/components/data-table/types";
import { writeTableParams } from "@/components/data-table/url-state";

export interface OrderViewDef {
  id: string;
  filters: FilterState;
  sort?: string;
}

export const ORDER_VIEWS: readonly OrderViewDef[] = [
  // 入金待ち (銀行振込) — 振込連絡 first
  {
    id: "awaiting_bank_transfer",
    filters: { view: "awaiting_bank_transfer" },
    sort: "notice_first",
  },
  // 準備待ち — paid, not packed yet
  {
    id: "to_prepare",
    filters: {
      paymentStatus: "paid",
      deliveryMethod: "ship",
      fulfillmentStatus: ["unfulfilled", "preparing"],
    },
  },
  // 発送待ち — packed, waiting for the carrier
  {
    id: "ready_to_ship",
    filters: { paymentStatus: "paid", deliveryMethod: "ship", fulfillmentStatus: "ready" },
  },
  // 店頭受取 — the customer can come
  { id: "pickup_ready", filters: { view: "pickup_ready" } },
  // 海外・書類待ち — export without its proof yet
  { id: "export_missing_evidence", filters: { view: "export_missing_evidence" } },
  // 返品・返金
  { id: "returns", filters: { view: "returns" } },
  // 要確認 — risk flags / held for a manager / sold twice
  { id: "flagged", filters: { view: "flagged" } },
  // everything paid and still to do before the carrier (the to-do inbox row "to ship")
  { id: "to_ship", filters: { view: "to_ship" } },
];

export function orderViews(t: TFunction): SavedView[] {
  return ORDER_VIEWS.map((view) => ({
    id: view.id,
    name: t(`orders:views.${view.id}`),
    filters: view.filters,
    sort: view.sort ?? null,
    system: true,
    pinned: view.id !== "to_ship",
  }));
}

export function viewById(id: string | null | undefined): OrderViewDef | undefined {
  return ORDER_VIEWS.find((view) => view.id === id);
}

/** Link to one view of the order table (filters written in the URL, like a saved view). */
export function orderViewLink(id: string): string {
  const view = viewById(id);
  const params = writeTableParams({
    filters: view?.filters ?? {},
    sort: view?.sort ?? null,
    view: id,
    limit: 25,
    cursor: null,
  });
  return `/orders?${params.toString()}`;
}
